import type { WebSocketMessage, WebSocketStatus } from '@/hooks/useWebSocket';

type BaseStepKey = 'submit' | 'queue' | 'download' | 'semantic' | 'parse' | 'schema_validation';
type StepState = 'complete' | 'current' | 'pending';

export type HomepageExtractionProgressItem = {
  key: string;
  label: string;
  description: string;
  state: StepState;
};

export type HomepageExtractionProgressModel = {
  currentStepKey: string;
  detail: string;
  headline: string;
  items: HomepageExtractionProgressItem[];
};

type HomepageExtractionProgressInput = {
  error?: string | null;
  isPending: boolean;
  messages: WebSocketMessage[];
  runStatus?: string | null;
  runStep?: string | null;
  socketStatus: WebSocketStatus;
  statusMessage?: string | null;
  waitingForResults: boolean;
};

const BASE_STEPS: Array<{
  description: string;
  key: Exclude<BaseStepKey, 'schema_validation'>;
  label: string;
}> = [
  {
    key: 'submit',
    label: 'Отправляем запрос',
    description: 'Проверяем параметры формы и создаём запуск извлечения.',
  },
  {
    key: 'queue',
    label: 'Готовим запуск',
    description: 'Запуск принят. Подключаемся к каналу статуса и ждём свободный слот.',
  },
  {
    key: 'download',
    label: 'Загружаем страницу',
    description: 'Открываем страницу, ждём загрузку HTML и проверочного скриншота.',
  },
  {
    key: 'semantic',
    label: 'Анализируем содержимое',
    description: 'Выделяем на странице нужные блоки и сигналы для извлечения.',
  },
  {
    key: 'parse',
    label: 'Собираем результат',
    description: 'Превращаем найденные фрагменты в структурированные данные.',
  },
];

const SCHEMA_VALIDATION_STEP = {
  key: 'schema_validation' as const,
  label: 'Проверяем структуру',
  description: 'Сверяем итоговый JSON со схемой и финализируем ответ.',
};

const SPECIAL_STEP_METADATA: Record<string, { description: string; label: string }> = {
  auth: {
    label: 'Проверяем доступ',
    description: 'Проверяем авторизацию и права на запуск извлечения.',
  },
  challenge: {
    label: 'Проверяем доступ к странице',
    description: 'Сайт запросил дополнительную проверку доступа или антибот-защиту.',
  },
  extract: {
    label: 'Извлекаем данные',
    description: 'Сервис выполняет основной этап обработки результата.',
  },
  internal: {
    label: 'Проверяем внутренний этап',
    description: 'Сервис обрабатывает внутренний шаг выполнения запроса.',
  },
  identity: {
    label: 'Подготавливаем браузерную сессию',
    description: 'Готовим защищённую браузерную сессию для доступа к странице.',
  },
  rate_limit: {
    label: 'Ждём свободный слот',
    description: 'Сервис ограничивает число одновременных запусков и готовит очередь.',
  },
  validation: {
    label: 'Проверяем входные данные',
    description: 'Уточняем параметры запуска перед основным извлечением.',
  },
  workflow: {
    label: 'Готовим workflow',
    description: 'Сервис собирает служебные данные перед основным извлечением.',
  },
  waiting_for_identity: {
    label: 'Подготавливаем браузерную сессию',
    description: 'Готовим защищённую браузерную сессию для доступа к странице.',
  },
};

function normalizeStep(step: string | null | undefined): string | null {
  const trimmed = step?.trim();
  return trimmed ? trimmed : null;
}

function normalizeStatusMessage(message: string | null | undefined): string | null {
  const trimmed = message?.trim();
  return trimmed ? trimmed : null;
}

function isBaseStepKey(step: string | null): step is BaseStepKey {
  return Boolean(step) && [...BASE_STEPS.map((item) => item.key), SCHEMA_VALIDATION_STEP.key].includes(step as BaseStepKey);
}

function buildBaseSteps(includeSchemaValidation: boolean) {
  return includeSchemaValidation ? [...BASE_STEPS, SCHEMA_VALIDATION_STEP] : BASE_STEPS;
}

function getLatestKnownStep(messages: WebSocketMessage[], runStep: string | null | undefined): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const candidate = normalizeStep(messages[index]?.step);
    if (candidate) {
      return candidate;
    }
  }

  return normalizeStep(runStep);
}

function getLastBaseStepKey(messages: WebSocketMessage[], runStep: string | null | undefined): BaseStepKey | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const candidate = normalizeStep(messages[index]?.step);
    if (isBaseStepKey(candidate)) {
      return candidate;
    }
  }

  const normalizedRunStep = normalizeStep(runStep);
  return isBaseStepKey(normalizedRunStep) ? normalizedRunStep : null;
}

function resolveSpecialStepMetadata(step: string, fallbackMessage: string | null) {
  const metadata = SPECIAL_STEP_METADATA[step];
  if (metadata) {
    return metadata;
  }

  return {
    label: `Текущий этап: ${step}`,
    description: fallbackMessage ?? 'Сервис выполняет дополнительный шаг обработки.',
  };
}

export function buildHomepageExtractionProgress(
  input: HomepageExtractionProgressInput,
): HomepageExtractionProgressModel | null {
  const fallbackMessage = normalizeStatusMessage(input.statusMessage);
  const stepHistory = [
    normalizeStep(input.runStep),
    ...input.messages.map((message) => normalizeStep(message.step)),
  ].filter((value): value is string => Boolean(value));
  const includeSchemaValidation = stepHistory.includes(SCHEMA_VALIDATION_STEP.key);
  const baseSteps = buildBaseSteps(includeSchemaValidation);
  const latestStep = getLatestKnownStep(input.messages, input.runStep);

  let currentBaseStep: BaseStepKey | null = null;
  let currentSpecialStep: string | null = null;

  if (input.isPending) {
    currentBaseStep = 'submit';
  } else if (isBaseStepKey(latestStep)) {
    currentBaseStep = latestStep;
  } else if (latestStep) {
    currentSpecialStep = latestStep;
  } else if (
    input.waitingForResults ||
    input.runStatus === 'queued' ||
    input.socketStatus === 'connecting' ||
    input.socketStatus === 'open'
  ) {
    currentBaseStep = 'queue';
  } else {
    return null;
  }

  const currentBaseIndex =
    currentBaseStep != null ? baseSteps.findIndex((item) => item.key === currentBaseStep) : -1;
  const specialAnchorStep =
    getLastBaseStepKey(input.messages, input.runStep) ??
    (input.isPending ? 'submit' : 'queue');
  const specialAnchorIndex = baseSteps.findIndex((item) => item.key === specialAnchorStep);

  const items = baseSteps.map<HomepageExtractionProgressItem>((item, index) => {
    let state: StepState = 'pending';

    if (currentBaseIndex >= 0) {
      if (index < currentBaseIndex) {
        state = 'complete';
      } else if (index === currentBaseIndex) {
        state = 'current';
      }
    } else if (index <= specialAnchorIndex) {
      state = 'complete';
    }

    return {
      key: item.key,
      label: item.label,
      description: item.description,
      state,
    };
  });

  let currentItem: HomepageExtractionProgressItem;

  if (currentBaseStep != null) {
    currentItem = items[Math.max(currentBaseIndex, 0)];
    if (currentBaseStep === 'queue' && input.waitingForResults) {
      currentItem = {
        ...currentItem,
        description: 'Запуск уже создан. Подключаемся к каналу статуса и ждём новые события от сервиса.',
      };
      items[Math.max(currentBaseIndex, 0)] = currentItem;
    }
  } else {
    const specialMetadata = resolveSpecialStepMetadata(currentSpecialStep!, fallbackMessage);
    currentItem = {
      key: currentSpecialStep!,
      label: specialMetadata.label,
      description: specialMetadata.description,
      state: 'current',
    };
    items.splice(Math.max(specialAnchorIndex, 0) + 1, 0, currentItem);
  }

  return {
    currentStepKey: currentItem.key,
    headline: currentItem.label,
    detail: currentItem.description,
    items,
  };
}
